import { FC } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { QuestionnaireDefinitionForm } from '@app/components/Admin/Questionnaires/QuestionnaireDefinitionForm';
import { PublishVersionDialog } from '@app/components/Admin/Questionnaires/PublishVersionDialog';
import { useQuestionnaireVersion } from '@app/view-creator/Admin/QuestionnaireAdminViewCreator';
import { formatPublishedAt, publicationStatusColors } from './AdminQuestionnaires';
import { useQuestionnaireEditorController } from './useQuestionnaireEditorController';

/** A past version, read-only. */
const VersionDialog: FC<{ formVersionId: number | null; onClose: () => void }> = ({ formVersionId, onClose }) => {
  const { data, isLoading, isError } = useQuestionnaireVersion(formVersionId);
  return (
    <Dialog open={!!formVersionId} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{data ? `${data.definition.name}, version ${data.version}` : 'Version'}</DialogTitle>
      <DialogContent>
        {isLoading && <CircularProgress />}
        {isError && <Alert severity="error">This version could not be loaded.</Alert>}
        {/* The dialog drops its top padding under the title, which would clip the first field's floating label. */}
        {data && (
          <Box sx={{ pt: 1 }}>
            <QuestionnaireDefinitionForm definition={data.definition} />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Creates a questionnaire or edits the latest version of one. Edits stay on this page until Publish, which runs the
 * copy-on-write versioning on the server. Past versions are read-only.
 */
export const AdminQuestionnaireEditor: FC = () => {
  const navigate = useNavigate();
  const routeFormId = useParams().formId;
  const formId = routeFormId === 'new' ? 0 : Number(routeFormId);
  const editor = useQuestionnaireEditorController(formId);

  if (editor.loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container component="section" sx={{ py: 2 }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/admin/questionnaires')} sx={{ mb: 1 }}>
        Back to Questionnaires
      </Button>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 1 }}>
        <Typography variant="h4">
          {editor.isNew ? 'New Questionnaire' : editor.published?.name || 'Questionnaire'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {editor.unpublishedChanges && <Chip label="Unpublished changes" color="warning" size="small" />}
          <Button onClick={editor.handleDiscard} disabled={!editor.unpublishedChanges || editor.publishing}>
            Discard Changes
          </Button>
          <Button
            variant="contained"
            onClick={editor.handlePublish}
            disabled={!editor.unpublishedChanges || editor.publishing}
          >
            Publish
          </Button>
        </Box>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Edits stay on this page until you publish, so leaving the page drops them. Publishing creates a new version;
        participants who already opened the current one finish it as they saw it.
      </Typography>

      {editor.loadFailed && <Alert severity="error">This questionnaire could not be loaded.</Alert>}
      {editor.message && (
        <Alert severity={editor.message.severity} sx={{ mb: 2 }}>
          {editor.message.text}
        </Alert>
      )}

      {!editor.loadFailed && (
        <QuestionnaireDefinitionForm
          definition={editor.definition}
          onChange={editor.setDefinition}
          published={editor.published}
        />
      )}

      {!editor.isNew && editor.versions.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" sx={{ mb: 1 }}>
            Version History
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Version</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Published</TableCell>
                <TableCell>Retired</TableCell>
                <TableCell>Reason for change</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {editor.versions.map((version) => (
                <TableRow key={version.id}>
                  <TableCell>v{version.version}</TableCell>
                  <TableCell>
                    <Chip size="small" label={version.status} color={publicationStatusColors[version.status]} />
                  </TableCell>
                  <TableCell>{formatPublishedAt(version.publishedAt)}</TableCell>
                  <TableCell>{formatPublishedAt(version.retiredAt)}</TableCell>
                  <TableCell sx={{ whiteSpace: 'pre-line' }}>
                    {version.changeReason ?? (
                      <Typography variant="body2" color="text.secondary">
                        {version.version === 1 ? 'First version' : 'Not recorded'}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => editor.setViewingVersionId(version.id)}>
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      <PublishVersionDialog
        open={editor.publishDialogOpen}
        version={editor.nextVersion}
        changeReason={editor.changeReason}
        onChangeReason={editor.setChangeReason}
        changedItems={editor.changedItems}
        answers={editor.meaningAnswers}
        onAnswer={editor.handleMeaningAnswer}
        onCancel={editor.handleCancelPublish}
        onPublish={editor.handleConfirmPublish}
        publishing={editor.publishing}
      />
      <VersionDialog formVersionId={editor.viewingVersionId} onClose={() => editor.setViewingVersionId(null)} />
    </Container>
  );
};
